import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#8B5CF6', '#F59E0B', '#10B981', '#EC4899', '#F97316', '#06B6D4', '#84CC16'];

// Date format helper - DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Convert DD-MM-YYYY to YYYY-MM-DD for input
const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) return dateStr; // Already YYYY-MM-DD
  const [day, month, year] = dateStr.split('-');
  return `${year}-${month}-${day}`;
};

// Get first day of current month in YYYY-MM-DD format (timezone safe)
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

// Get today in YYYY-MM-DD format (timezone safe)
const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function LeadSourceReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [dateRange, setDateRange] = React.useState({
    from: getFirstDayOfMonth(),
    to: getToday()
  });

  React.useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('date_from', dateRange.from);
      if (dateRange.to) params.append('date_to', dateRange.to);
      
      const response = await axios.get(`${BACKEND_URL}/api/leads/analytics/source-performance?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    }
    setLoading(false);
  };

  // Calculate totals
  const totals = React.useMemo(() => {
    return data.reduce((acc, item) => ({
      total: acc.total + item.total,
      contacted: acc.contacted + item.contacted,
      interested: acc.interested + item.interested,
      test_drive: acc.test_drive + item.test_drive,
      won: acc.won + item.won,
      lost: acc.lost + item.lost
    }), { total: 0, contacted: 0, interested: 0, test_drive: 0, won: 0, lost: 0 });
  }, [data]);

  // Prepare chart data
  const pieData = data.map((item, idx) => ({
    name: item.source,
    value: item.total,
    color: COLORS[idx % COLORS.length]
  }));

  const conversionData = data.map(item => ({
    name: item.source.length > 10 ? item.source.substring(0, 10) + '...' : item.source,
    'Conversion Rate': item.conversion_rate,
    'Total Leads': item.total,
    'Won': item.won
  }));

  // Export to Excel
  const exportToExcel = () => {
    const exportData = data.map(item => ({
      'Lead Source': item.source,
      'Total Leads': item.total,
      'Contacted': item.contacted,
      'Interested': item.interested,
      'Test Drive': item.test_drive,
      'Won': item.won,
      'Lost': item.lost,
      'Conversion Rate %': item.conversion_rate
    }));
    
    // Add totals row
    exportData.push({
      'Lead Source': 'TOTAL',
      'Total Leads': totals.total,
      'Contacted': totals.contacted,
      'Interested': totals.interested,
      'Test Drive': totals.test_drive,
      'Won': totals.won,
      'Lost': totals.lost,
      'Conversion Rate %': totals.total > 0 ? ((totals.won / totals.total) * 100).toFixed(1) : '0'
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lead Source Report');
    XLSX.writeFile(wb, `Lead_Source_Report_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success('Report exported to Excel');
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Lead Source Analysis">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="report-title">Lead Source Analysis</h2>
            <p className="text-sm text-gray-500">Analyze lead performance by source channel • <span className="font-medium text-gray-700">{data.length} sources, {totals.total} total leads</span></p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="date-to"
              />
            </div>
            <div className="relative group">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                📥 Export
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border hidden group-hover:block z-10">
                <button onClick={exportToExcel} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📊 Export to Excel</button>
                <button onClick={() => window.print()} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📄 Print/PDF</button>
              </div>
            </div>
            <button
              onClick={() => navigate('/leads')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{totals.total}</p>
                <p className="text-sm text-blue-600">Total Leads</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <p className="text-2xl font-bold text-purple-700">{totals.contacted}</p>
                <p className="text-sm text-purple-600">Contacted</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-2xl font-bold text-yellow-700">{totals.interested}</p>
                <p className="text-sm text-yellow-600">Interested</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <p className="text-2xl font-bold text-teal-700">{totals.test_drive}</p>
                <p className="text-sm text-teal-600">Test Drive</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-2xl font-bold text-green-700">{totals.won}</p>
                <p className="text-sm text-green-600">Won</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-2xl font-bold text-red-700">{totals.lost}</p>
                <p className="text-sm text-red-600">Lost</p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Lead Distribution by Source</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart - Conversion Rate */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Conversion Rate by Source</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={conversionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Conversion Rate" fill="#22C55E" name="Conversion %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">Source-wise Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="source-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Contacted</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Interested</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Test Drive</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Won</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Lost</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((item, idx) => (
                      <tr key={item.source} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                            <span className="font-medium text-gray-800">{item.source}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center font-medium">{item.total}</td>
                        <td className="px-6 py-4 text-center">{item.contacted}</td>
                        <td className="px-6 py-4 text-center">{item.interested}</td>
                        <td className="px-6 py-4 text-center">{item.test_drive}</td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">{item.won}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm">{item.lost}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                            item.conversion_rate >= 30 ? 'bg-green-100 text-green-700' :
                            item.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.conversion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-6 py-4">TOTAL</td>
                      <td className="px-6 py-4 text-center">{totals.total}</td>
                      <td className="px-6 py-4 text-center">{totals.contacted}</td>
                      <td className="px-6 py-4 text-center">{totals.interested}</td>
                      <td className="px-6 py-4 text-center">{totals.test_drive}</td>
                      <td className="px-6 py-4 text-center">{totals.won}</td>
                      <td className="px-6 py-4 text-center">{totals.lost}</td>
                      <td className="px-6 py-4 text-center">
                        {totals.total > 0 ? ((totals.won / totals.total) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </LeadsLayout>
  );
}

export default LeadSourceReport;
